import { Position, type Edge, type Node } from '@xyflow/react';
import {
  describeTransformArity,
  formatWorkflowExpressionAst,
  getTransformDescription,
  isTransform,
  parseWorkflowExpression,
  readManualExpressionOverrides,
  type WorkflowExpressionAst,
  type ZipTieWire,
} from '@erp/workflows';
import type {
  StepNode,
  StepNodeData,
} from 'src/pages/authenticated/workflows/components/config-renderers/types';
import {
  astToBuilder,
  builderToAst,
  type BuilderNode,
} from './expressionBuilder/builderTree';
import { functionLabel } from './expressionBuilder/builderFunctions';

/**
 * Visual language for socket expressions on the canvas.
 *
 * An expression that is nothing but wiring — a lone `(output ...)` — already
 * has a perfectly good visual form: an edge. Anything that TRANSFORMS the data
 * on the way in does not, so every such socket gets ONE badge, placed just
 * before the socket, where all of its expression's wires converge:
 *
 * ```
 *   o- - -.email- -\
 *                   - -[join]--o socket
 *   o- - - - - - - -/
 * ```
 *
 * The badge carries the ROOT call's glyph (`join`, `fallback`, `[ ]`); nested
 * calls never get a glyph of their own — their sources wire straight into the
 * root's badge, and the builder (opened by clicking the badge) shows the
 * nesting. A field read off a source (`get`) rides its wire as a label.
 *
 * Overlay nodes and edges are DERIVED, never state: they are appended at render
 * time from `config.__manual_expression_overrides`, so nothing here can reach
 * `graphToDefinition`, the undo stack, or a save. They are not selectable,
 * draggable, deletable, or connectable — the way to change one is to edit the
 * expression that produced it.
 *
 * Only manual overrides are drawn. Auto-derived expressions are, by
 * construction, exactly the edges already on the canvas.
 */

export const EXPRESSION_NODE_TYPE = 'expressionNode';

const NODE_ID_PREFIX = 'expr-node:';
const EDGE_ID_PREFIX = 'expr-edge:';

/** Gap between the badge's right edge and the socket it feeds. */
const BADGE_GAP = 40;
/**
 * Fallback socket spacing when the canvas has not measured the step's handles
 * yet (first frame, or the read-only view): roughly a socket row, so two
 * badges on one step never land on top of each other.
 */
const FALLBACK_HEADER_DY = 56;
const FALLBACK_SOCKET_DY = 24;

/** Advance width of the badge's 10px monospace text (Commit Mono: 0.6em). */
const CHAR_WIDTH = 6;
/** 5px of padding on each side, plus the border. */
const NODE_PADDING_X = 12;
/**
 * The badge's height, fixed: every edge meets it at its vertical center, so
 * nothing about the badge needs to grow with its argument count.
 */
export const EXPRESSION_NODE_HEIGHT = 16;

/**
 * Overlay nodes carry explicit dimensions rather than being measured, so the
 * canvas can lay out and route to them on their very first frame — they are
 * appended at render time and never live in node state, so there is nowhere for
 * a measured size to be written back to.
 */
function badgeWidth(label: string): number {
  return label.length * CHAR_WIDTH + NODE_PADDING_X;
}

/**
 * The badge's two handles, declared up front. React Flow drops the measured
 * handle bounds of any node handed to it without `measured`, and a derived
 * node never has one — so without these every wire into or out of a badge
 * silently fails to draw. Each sits at the badge's vertical center.
 */
export function badgeHandles(width: number): NonNullable<Node['handles']> {
  const y = EXPRESSION_NODE_HEIGHT / 2 - 0.5;
  return [
    {
      id: EXPRESSION_INPUT_HANDLE_ID,
      type: 'target',
      position: Position.Left,
      x: 0,
      y,
      width: 1,
      height: 1,
    },
    {
      id: EXPRESSION_OUTPUT_HANDLE_ID,
      type: 'source',
      position: Position.Right,
      x: width - 1,
      y,
      width: 1,
      height: 1,
    },
  ];
}

/** One argument of a call, described for the hover card. */
export interface ExpressionArgumentInfo {
  /** 1-based position, as the reader counts them. */
  position: number;
  /** Where the value comes from: a step's socket, a literal, or a nested call. */
  source: string;
  kind: 'socket' | 'literal' | 'call';
}

/** How the badge reads relative to the socket the builder has open. */
export type ExpressionBadgeState = 'idle' | 'editing' | 'dimmed';

export interface ExpressionNodeData extends Record<string, unknown> {
  /** The root call's glyph — `join` for `string_join`, `[ ]` for `collect`. */
  label: string;
  /** The root call's full name, or `literal`. Shown on hover. */
  name: string;
  /**
   * The whole expression, with step ids replaced by step NAMES. The stored
   * form references uuids, which say nothing to a reader.
   */
  expression: string;
  /** What the root call does, when it is a registered transform. */
  description?: string;
  /** How many arguments it takes, in words. */
  arity?: string;
  /** Every root argument in order, resolved to something readable. */
  args: ExpressionArgumentInfo[];
  /** The socket the badge feeds, so a click can open its builder. */
  targetNodeId: string;
  targetSocketId: string;
  /** The socket's display name, for the badge's accessible label. */
  targetSocketName: string;
  state: ExpressionBadgeState;
  /**
   * Unused by the badge, and present only so an overlay node satisfies the
   * canvas's node-data type alongside the step nodes it is concatenated with.
   */
  inputs: StepNodeData['inputs'];
  outputs: StepNodeData['outputs'];
}

export const EXPRESSION_INPUT_HANDLE_ID = 'in';
export const EXPRESSION_OUTPUT_HANDLE_ID = 'out';

export interface ExpressionOverlay {
  nodes: StepNode[];
  edges: Edge[];
  /**
   * Stored edges feeding an overridden socket. An expression REPLACES the edge
   * into the socket it names (see `resolveActiveEdges`), so drawing both would
   * show a data path the engine will not run.
   */
  hiddenEdgeIds: Set<string>;
}

export interface ExpressionOverlayOptions {
  /**
   * Measured absolute centers of input handles, keyed
   * {@link socketAnchorKey}. Missing entries fall back to an estimate.
   */
  anchors?: ReadonlyMap<string, { x: number; y: number }>;
  /** The socket the builder is open on: its badge and wires turn blue. */
  editing?: { nodeId: string; socketId: string } | null;
  /**
   * The open builder's session: that socket's badge and wires are drawn from
   * the tree being edited, not the stored override, so every pick shows up on
   * the canvas straight away — even while slots are still empty.
   */
  live?: LiveExpressionInput | null;
}

export interface LiveExpressionInput {
  target: { nodeId: string; socketId: string };
  tree: BuilderNode;
}

export function socketAnchorKey(nodeId: string, socketId: string): string {
  return `${nodeId}\u0000${socketId}`;
}

const EMPTY_OVERLAY: ExpressionOverlay = {
  nodes: [],
  edges: [],
  hiddenEdgeIds: new Set(),
};

/** Stands in for a call name on a badge that is a bare literal, not a call. */
const LITERAL_BADGE_NAME = 'literal';

/**
 * What a badge's root does, in one sentence. Transforms carry their own; the
 * two structural cases are described here because they are grammar, not
 * registry entries.
 */
function describeCall(name: string): string | undefined {
  if (name === 'collect') {
    return 'Gathers its inputs into one array, dropping any that are empty.';
  }
  if (name === LITERAL_BADGE_NAME) {
    return 'A fixed value, written into the expression itself.';
  }
  return isTransform(name) ? getTransformDescription(name) : undefined;
}

/** A step's display name, falling back to its id when it is off-canvas. */
function stepLabel(stepId: string, nodesById: Map<string, Node>): string {
  const label = (nodesById.get(stepId)?.data as Partial<StepNodeData>)?.label;
  return typeof label === 'string' && label.length > 0 ? label : stepId;
}

function socketName(node: Node, socketId: string): string {
  const inputs = (node.data as Partial<StepNodeData>).inputs;
  const name = inputs?.[socketId]?.name;
  return typeof name === 'string' && name.length > 0 ? name : socketId;
}

/** One argument resolved to something a reader can act on. */
function describeArgument(
  ast: WorkflowExpressionAst,
  index: number,
  nodesById: Map<string, Node>,
): ExpressionArgumentInfo {
  const position = index + 1;
  const node = astToBuilder(ast);
  if (node.kind === 'source') {
    return {
      position,
      kind: 'socket',
      source: `${stepLabel(node.stepId, nodesById)} › ${node.socketId}${
        node.field ? `.${node.field}` : ''
      }`,
    };
  }
  if (node.kind === 'call') {
    return { position, kind: 'call', source: `${functionLabel(node.fn)}(…)` };
  }
  return {
    position,
    kind: 'literal',
    source: formatWorkflowExpressionAst(ast),
  };
}

/**
 * A node's position in canvas space. Step nodes inside a layout group store
 * their position relative to that group, but overlay nodes are parentless, so
 * both sides have to be compared in the same space.
 */
function absolutePosition(
  node: Node,
  nodesById: Map<string, Node>,
): { x: number; y: number } {
  let x = node.position.x;
  let y = node.position.y;
  const seen = new Set<string>([node.id]);
  let parentId = node.parentId;
  while (parentId && !seen.has(parentId)) {
    seen.add(parentId);
    const parent = nodesById.get(parentId);
    if (!parent) break;
    x += parent.position.x;
    y += parent.position.y;
    parentId = parent.parentId;
  }
  return { x, y };
}

const EDGE_STYLE_BY_STATE: Record<ExpressionBadgeState, Edge['style']> = {
  // Greyscale and dashed: an expression-driven wire is still a wire, and the
  // dash is enough to say "derived" without coloring it like a warning.
  idle: { stroke: 'var(--color-gray-500)', strokeDasharray: '5 4' },
  editing: { stroke: 'var(--color-blue-600)', strokeWidth: 2 },
  dimmed: { stroke: 'var(--color-gray-300)', strokeDasharray: '5 4' },
};

function expressionEdge(
  id: string,
  source: string,
  sourceHandle: string,
  target: { nodeId: string; handleId: string },
  state: ExpressionBadgeState,
  field?: string,
  route?: ZipTieWire,
): Edge {
  return {
    id,
    source,
    sourceHandle,
    target: target.nodeId,
    targetHandle: target.handleId,
    // Plain edge rather than the canvas's `deletable` type: an overlay edge
    // is derived, so it must not offer a delete affordance. `zipTieWire` names
    // the sockets a step-to-socket wire joins, so a zip tie can hold it; the
    // badge's short stub carries null, so it is never read as a strand.
    type: 'default',
    data: { zipTieWire: route ?? null },
    selectable: false,
    focusable: false,
    deletable: false,
    className: 'workflow-expression-edge',
    style: EDGE_STYLE_BY_STATE[state],
    // A field access rides its wire rather than getting a node: on the
    // canvas `get` is never a block.
    ...(field
      ? {
          label: `.${field}`,
          labelStyle: {
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            fill:
              state === 'editing'
                ? 'var(--color-blue-700)'
                : 'var(--color-gray-700)',
          },
          labelBgPadding: [4, 2] as [number, number],
          labelBgBorderRadius: 3,
          labelBgStyle: {
            fill: 'var(--color-background, white)',
            stroke:
              state === 'editing'
                ? 'var(--color-blue-600)'
                : 'var(--color-gray-400)',
          },
        }
      : {}),
  };
}

/** Every source the expression reads, with the field read off it, if any. */
function collectWires(
  node: BuilderNode,
  path: string,
  out: Array<{
    stepId: string;
    socketId: string;
    field?: string;
    path: string;
  }>,
): void {
  if (node.kind === 'source') {
    out.push({ ...node, path });
    return;
  }
  if (node.kind === 'call') {
    node.args.forEach((arg, index) =>
      collectWires(arg, `${path}.${index}`, out),
    );
  }
}

function badgeState(
  nodeId: string,
  socketId: string,
  editing: ExpressionOverlayOptions['editing'],
): ExpressionBadgeState {
  if (!editing) return 'idle';
  return editing.nodeId === nodeId && editing.socketId === socketId
    ? 'editing'
    : 'dimmed';
}

/**
 * The center of an input socket's handle in canvas space: measured when the
 * canvas has it, otherwise estimated from the socket's position in the list.
 */
function socketAnchor(
  node: Node,
  socketId: string,
  socketIndex: number,
  nodesById: Map<string, Node>,
  anchors: ExpressionOverlayOptions['anchors'],
): { x: number; y: number } {
  const measured = anchors?.get(socketAnchorKey(node.id, socketId));
  if (measured) return measured;
  const position = absolutePosition(node, nodesById);
  return {
    x: position.x,
    y: position.y + FALLBACK_HEADER_DY + socketIndex * FALLBACK_SOCKET_DY,
  };
}

interface SocketOverlayInput {
  node: Node;
  socketId: string;
  socketIndex: number;
  tree: BuilderNode;
  /** `null` while a live session still has empty slots. */
  ast: WorkflowExpressionAst | null;
}

/** Stands in for the hover text of an expression that is still being filled. */
const INCOMPLETE_EXPRESSION = '…';

/** The badge's one word: the root call's glyph, a `.field`, or the literal. */
function badgeLabel(
  tree: BuilderNode,
  ast: WorkflowExpressionAst | null,
): string {
  if (tree.kind === 'call') return functionLabel(tree.fn);
  if (tree.kind === 'source') return `.${tree.field ?? ''}`;
  return ast ? formatWorkflowExpressionAst(ast) : INCOMPLETE_EXPRESSION;
}

function badgeData(
  { node, socketId, tree, ast }: SocketOverlayInput,
  label: string,
  state: ExpressionBadgeState,
  nodesById: Map<string, Node>,
): ExpressionNodeData {
  // The AST's name where there is one: a root `get` is a source in the tree,
  // but its tooltip must still describe a field lookup.
  const name =
    ast?.kind === 'call'
      ? ast.name
      : tree.kind === 'call'
        ? tree.fn
        : LITERAL_BADGE_NAME;
  return {
    label,
    name,
    // Step NAMES, not the stored uuids — a hover card full of
    // `(output "3f2a…" "greeting")` tells the reader nothing.
    expression: ast
      ? formatWorkflowExpressionAst(ast, (stepId) =>
          stepLabel(stepId, nodesById),
        )
      : INCOMPLETE_EXPRESSION,
    description: describeCall(name),
    arity: isTransform(name) ? describeTransformArity(name) : undefined,
    args:
      ast?.kind === 'call' && tree.kind === 'call'
        ? ast.args.map((arg, index) => describeArgument(arg, index, nodesById))
        : [],
    targetNodeId: node.id,
    targetSocketId: socketId,
    targetSocketName: socketName(node, socketId),
    state,
    inputs: {},
    outputs: {},
  };
}

/**
 * Every level's sources converge on the one badge. A reference to a step
 * that is not on the canvas is skipped rather than drawn. The SOCKET is
 * deliberately not checked: a dynamic-socket step (a CDC observe trigger)
 * persists only its static base sockets, so gating on `data.outputs` would
 * drop every per-column wire. React Flow already ignores an edge whose
 * handle is not rendered.
 */
function badgeInputEdges(
  tree: BuilderNode,
  keyPrefix: string,
  badgeId: string,
  state: ExpressionBadgeState,
  nodesById: Map<string, Node>,
  target: { stepId: string; socketId: string },
): Edge[] {
  const wires: Parameters<typeof collectWires>[2] = [];
  collectWires(tree, 'root', wires);
  return wires.flatMap((wire) =>
    nodesById.has(wire.stepId)
      ? [
          expressionEdge(
            `${EDGE_ID_PREFIX}${keyPrefix}:${wire.path}`,
            wire.stepId,
            wire.socketId,
            { nodeId: badgeId, handleId: EXPRESSION_INPUT_HANDLE_ID },
            state,
            wire.field,
            {
              sourceStepId: wire.stepId,
              sourceSocketId: wire.socketId,
              targetStepId: target.stepId,
              targetSocketId: target.socketId,
            },
          ),
        ]
      : [],
  );
}

function emitSocketOverlay(
  input: SocketOverlayInput,
  nodesById: Map<string, Node>,
  options: ExpressionOverlayOptions,
  out: { nodes: StepNode[]; edges: Edge[] },
): void {
  const { node, socketId, socketIndex, tree, ast } = input;
  const keyPrefix = `${node.id}:${socketId}`;
  const state = badgeState(node.id, socketId, options.editing);
  const socketTarget = { nodeId: node.id, handleId: socketId };

  // A lone reference IS an edge — no badge says anything the wire doesn't.
  if (tree.kind === 'source' && !tree.field) {
    if (nodesById.has(tree.stepId)) {
      out.edges.push(
        expressionEdge(
          `${EDGE_ID_PREFIX}${keyPrefix}:root`,
          tree.stepId,
          tree.socketId,
          socketTarget,
          state,
          undefined,
          {
            sourceStepId: tree.stepId,
            sourceSocketId: tree.socketId,
            targetStepId: node.id,
            targetSocketId: socketId,
          },
        ),
      );
    }
    return;
  }

  const label = badgeLabel(tree, ast);
  const nodeId = `${NODE_ID_PREFIX}${keyPrefix}`;
  const width = badgeWidth(label);
  const anchor = socketAnchor(
    node,
    socketId,
    socketIndex,
    nodesById,
    options.anchors,
  );

  out.nodes.push({
    id: nodeId,
    type: EXPRESSION_NODE_TYPE,
    position: {
      x: anchor.x - BADGE_GAP - width,
      y: anchor.y - EXPRESSION_NODE_HEIGHT / 2,
    },
    width,
    height: EXPRESSION_NODE_HEIGHT,
    handles: badgeHandles(width),
    selectable: false,
    draggable: false,
    deletable: false,
    connectable: false,
    zIndex: 10,
    data: badgeData(input, label, state, nodesById) as unknown as StepNodeData,
  });

  out.edges.push(
    expressionEdge(
      `${EDGE_ID_PREFIX}${keyPrefix}:out`,
      nodeId,
      EXPRESSION_OUTPUT_HANDLE_ID,
      socketTarget,
      state,
    ),
    ...badgeInputEdges(tree, keyPrefix, nodeId, state, nodesById, {
      stepId: node.id,
      socketId,
    }),
  );
}

/** Stored edges into a socket an expression (stored or live) replaces. */
function hideEdgesInto(
  nodeId: string,
  socketId: string,
  edges: readonly Edge[],
  hiddenEdgeIds: Set<string>,
): void {
  for (const edge of edges) {
    if (edge.target === nodeId && edge.targetHandle === socketId) {
      hiddenEdgeIds.add(String(edge.id));
    }
  }
}

/**
 * The open builder's session, drawn exactly like a stored override: the
 * session's tree is what the socket will get, so stored edges into it are
 * hidden and its badge and wires show each edit as it happens. An empty
 * session draws nothing; an incomplete one still shows its root and the
 * sources picked so far.
 */
function emitLiveSocket(
  live: LiveExpressionInput,
  nodesById: Map<string, Node>,
  edges: readonly Edge[],
  options: ExpressionOverlayOptions,
  out: { nodes: StepNode[]; edges: Edge[] },
  hiddenEdgeIds: Set<string>,
): void {
  const { nodeId, socketId } = live.target;
  const node = nodesById.get(nodeId);
  if (!node) return;
  hideEdgesInto(nodeId, socketId, edges, hiddenEdgeIds);
  if (live.tree.kind === 'empty') return;
  const inputs = Object.values(
    (node.data as Partial<StepNodeData>).inputs ?? {},
  );
  emitSocketOverlay(
    {
      node,
      socketId,
      socketIndex: Math.max(
        0,
        inputs.findIndex((socket) => socket.id === socketId),
      ),
      tree: live.tree,
      ast: builderToAst(live.tree),
    },
    nodesById,
    options,
    out,
  );
}

/**
 * Derive the expression overlay for a canvas: one badge per manually
 * overridden input socket, the wires converging on it, and the stored edges
 * those overrides have replaced.
 */
export function buildExpressionOverlay(
  nodes: Node[],
  edges: Edge[],
  options: ExpressionOverlayOptions = {},
): ExpressionOverlay {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const out = { nodes: [] as StepNode[], edges: [] as Edge[] };
  const hiddenEdgeIds = new Set<string>();

  for (const node of nodes) {
    const data = node.data as Partial<StepNodeData>;
    const overrides = readManualExpressionOverrides(data.config);
    const inputs = Object.values(data.inputs ?? {});

    inputs.forEach((socket, socketIndex) => {
      const expression = overrides[socket.id];
      if (typeof expression !== 'string') return;
      // The open socket is drawn from the live session below instead.
      if (
        options.live?.target.nodeId === node.id &&
        options.live.target.socketId === socket.id
      ) {
        return;
      }

      let ast: WorkflowExpressionAst;
      try {
        ast = parseWorkflowExpression(expression);
      } catch {
        // An unparseable override can't be drawn. It also can't have been
        // saved through the editor (the builder validates first), so this
        // only covers hand-edited data — leave the wiring edges visible.
        return;
      }

      hideEdgesInto(node.id, socket.id, edges, hiddenEdgeIds);
      emitSocketOverlay(
        {
          node,
          socketId: socket.id,
          socketIndex,
          tree: astToBuilder(ast),
          ast,
        },
        nodesById,
        options,
        out,
      );
    });
  }

  if (options.live) {
    emitLiveSocket(options.live, nodesById, edges, options, out, hiddenEdgeIds);
  }

  if (
    out.nodes.length === 0 &&
    out.edges.length === 0 &&
    hiddenEdgeIds.size === 0
  ) {
    // A stable empty value, so the render-layer memos downstream can skip
    // rebuilding their arrays on every unrelated node change.
    return EMPTY_OVERLAY;
  }
  return { nodes: out.nodes, edges: out.edges, hiddenEdgeIds };
}

/**
 * A canvas with its expression overlay folded in: the badges appended, and
 * the stored edges their expressions replaced dropped. Both the editor and
 * the read-only view render through this, so an override looks the same
 * wherever the graph is drawn.
 *
 * Call it on the arrays about to be handed to React Flow. The overlay is
 * derived per call, so memoize it at the call site.
 */
export function withExpressionOverlay<TNode extends Node>(
  nodes: TNode[],
  edges: Edge[],
  options?: ExpressionOverlayOptions,
): { nodes: Array<TNode | StepNode>; edges: Edge[] } {
  const overlay = buildExpressionOverlay(nodes, edges, options);
  if (overlay === EMPTY_OVERLAY) return { nodes, edges };
  return {
    nodes: [...nodes, ...overlay.nodes],
    edges: [
      ...edges.filter((edge) => !overlay.hiddenEdgeIds.has(String(edge.id))),
      ...overlay.edges,
    ],
  };
}
